import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  cancelCustomerJob,
  createCustomerJob,
  dismissCustomerJob,
  getCustomerJobEncryption,
  getCustomerJobEvents,
  listCustomerJobSessions,
  listCustomerJobs,
  type CustomerDeploymentStatus,
  type CustomerJob,
  type CustomerJobEvent,
  type CustomerJobSession,
  type CustomerJobType,
} from './api.js';
import {
  decryptCustomerJobEvent,
  encryptCustomerJobPayload,
  rememberCustomerJobKey,
} from './customer-job-crypto.js';

const terminalStatuses = new Set([
  'succeeded',
  'failed',
  'dead_letter',
  'cancelled',
  'expired',
]);
const activeStatuses = new Set(['queued', 'leased', 'running', 'retry_wait', 'cancelling']);

export function CustomerJobsDialog({
  installation,
  onClose,
}: {
  installation: CustomerDeploymentStatus;
  onClose: () => void;
}) {
  const [jobs, setJobs] = useState<CustomerJob[]>([]);
  const [sessions, setSessions] = useState<CustomerJobSession[]>([]);
  const [jobType, setJobType] = useState<CustomerJobType>('session.start');
  const [sessionId, setSessionId] = useState('');
  const [title, setTitle] = useState('');
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [outputEvents, setOutputEvents] = useState<DecryptedEvent[]>([]);
  const [outputError, setOutputError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const outputEnd = useRef<HTMLDivElement | null>(null);

  const refresh = async () => {
    const [nextJobs, nextSessions] = await Promise.all([
      listCustomerJobs(installation.installationId),
      listCustomerJobSessions(installation.installationId),
    ]);
    setJobs(nextJobs);
    setSessions(nextSessions);
  };

  useEffect(() => {
    let current = true;
    let timer: number | undefined;
    const poll = async () => {
      try {
        const [nextJobs, nextSessions] = await Promise.all([
          listCustomerJobs(installation.installationId),
          listCustomerJobSessions(installation.installationId),
        ]);
        if (current) {
          setJobs(nextJobs);
          setSessions(nextSessions);
        }
      } catch (reason) {
        if (current) setError(reason instanceof Error ? reason.message : String(reason));
      }
      if (current) timer = window.setTimeout(poll, 3_000);
    };
    void poll();
    return () => {
      current = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [installation.installationId]);

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);

  const selectableSessions = useMemo(
    () => sessions.filter((session) => session.status === 'active'),
    [sessions],
  );
  useEffect(() => {
    if (
      jobType !== 'session.start'
      && !selectableSessions.some((session) => session.sessionId === sessionId)
    ) {
      setSessionId(selectableSessions[0]?.sessionId ?? '');
    }
  }, [jobType, sessionId, selectableSessions]);

  useEffect(() => {
    if (jobs.length === 0) {
      if (selectedJobId !== null) setSelectedJobId(null);
      return;
    }
    if (!selectedJobId || !jobs.some((job) => job.jobId === selectedJobId)) {
      setSelectedJobId(jobs[0]!.jobId);
    }
  }, [jobs, selectedJobId]);

  useEffect(() => {
    setOutputEvents([]);
    setOutputError(null);
    setCopyState('idle');
    if (!selectedJobId) return;
    let current = true;
    let timer: number | undefined;
    let after = 0;
    const poll = async () => {
      try {
        const events = await getCustomerJobEvents(selectedJobId, after);
        const decrypted = await Promise.all(events.map(decryptEvent));
        if (current && decrypted.length > 0) {
          after = Math.max(after, ...decrypted.map((event) => event.id));
          setOutputEvents((existing) => mergeEvents(existing, decrypted));
          setOutputError(null);
        }
      } catch (reason) {
        if (current) {
          setOutputError(reason instanceof Error ? reason.message : String(reason));
        }
      }
      if (current) timer = window.setTimeout(poll, 1_500);
    };
    void poll();
    return () => {
      current = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [selectedJobId]);

  useEffect(() => {
    outputEnd.current?.scrollIntoView?.({ block: 'end' });
  }, [outputEvents]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy('publish');
    setError(null);
    let contentKey: Uint8Array | null = null;
    try {
      let payload = null;
      if (jobType !== 'session.stop') {
        const encryption = await getCustomerJobEncryption(installation.installationId);
        const encrypted = await encryptCustomerJobPayload(
          encryption,
          jobType === 'session.start'
            ? {
                title: title.trim() || 'Customer agent session',
                prompt: prompt.trim(),
              }
            : { prompt: prompt.trim() },
        );
        payload = encrypted.envelope;
        contentKey = encrypted.contentKey;
      }
      const job = await createCustomerJob({
        installationId: installation.installationId,
        jobType,
        ...(jobType === 'session.start' ? {} : { sessionId }),
        idempotencyKey: crypto.randomUUID(),
        payload,
      });
      if (contentKey) rememberCustomerJobKey(job.jobId, contentKey);
      setPrompt('');
      setTitle('');
      setSelectedJobId(job.jobId);
      setJobs((current) => [job, ...current]);
      await refresh();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      contentKey?.fill(0);
      setBusy(null);
    }
  };

  const mutate = async (
    job: CustomerJob,
    action: 'cancel' | 'dismiss',
  ) => {
    setBusy(job.jobId);
    setError(null);
    try {
      const updated = action === 'cancel'
        ? await cancelCustomerJob(job.jobId)
        : await dismissCustomerJob(job.jobId);
      setJobs((current) => action === 'dismiss'
        ? current.filter((candidate) => candidate.jobId !== job.jobId)
        : current.map((candidate) => candidate.jobId === updated.jobId ? updated : candidate));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(null);
    }
  };

  const copyOutput = async () => {
    const transcript = outputEntries
      .filter((entry) => entry.text)
      .map((entry) => `${entry.label}: ${entry.text}`)
      .join('\n\n');
    if (!transcript) return;
    try {
      await navigator.clipboard.writeText(transcript);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };

  const needsSession = jobType !== 'session.start';
  const canSubmit = jobType === 'session.stop'
    ? Boolean(sessionId)
    : Boolean(prompt.trim()) && (!needsSession || Boolean(sessionId));
  const selectedJob = jobs.find((job) => job.jobId === selectedJobId) ?? null;
  const outputEntries = useMemo(() => coalesceOutput(outputEvents), [outputEvents]);

  return (
    <div
      className="jobs-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="jobs-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="customer-jobs-title"
      >
        <header className="jobs-heading">
          <div>
            <span className="jobs-kicker"><i /> Customer VPC job queue</span>
            <h2 id="customer-jobs-title">Agent sessions</h2>
            <p>
              Publish encrypted work to the elastic worker pool. Session content is encrypted
              in this browser and can only be opened by your customer-owned KMS key.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close agent jobs">×</button>
        </header>

        <div className="jobs-grid">
          <form className="job-publisher" onSubmit={submit}>
            <div>
              <span className="eyebrow">Publish a job</span>
              <h3>What should the VPC worker do?</h3>
            </div>
            <div className="job-type-tabs" role="radiogroup" aria-label="Job type">
              {([
                ['session.start', 'Start'],
                ['session.prompt', 'Prompt'],
                ['session.stop', 'Stop'],
              ] as const).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={jobType === value}
                  className={jobType === value ? 'selected' : ''}
                  onClick={() => setJobType(value)}
                >
                  {label}
                </button>
              ))}
            </div>
            {needsSession && (
              <label>
                <span>Session</span>
                <select
                  aria-label="Customer agent session"
                  value={sessionId}
                  onChange={(event) => setSessionId(event.target.value)}
                >
                  {selectableSessions.length === 0 && <option value="">No active sessions</option>}
                  {selectableSessions.map((session) => (
                    <option key={session.sessionId} value={session.sessionId}>
                      {shortId(session.sessionId)}
                    </option>
                  ))}
                </select>
                <small>Prompts are fenced to this session’s original logical MicroVM.</small>
              </label>
            )}
            {jobType === 'session.start' && (
              <label>
                <span>Session name <em>optional</em></span>
                <input
                  maxLength={120}
                  placeholder="Repository triage"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                />
              </label>
            )}
            {jobType !== 'session.stop' && (
              <label>
                <span>Prompt</span>
                <textarea
                  autoFocus
                  maxLength={32_000}
                  rows={7}
                  placeholder={jobType === 'session.start'
                    ? 'Describe the task for the new isolated session…'
                    : 'Send the next instruction to this session…'}
                  value={prompt}
                  onChange={(event) => setPrompt(event.target.value)}
                />
                <small>{prompt.length.toLocaleString()} / 32,000 characters</small>
              </label>
            )}
            {jobType === 'session.stop' && (
              <div className="job-stop-note">
                <strong>Stop and retain</strong>
                <span>The MicroVM is terminated. Customer-owned history and snapshots remain available under your retention policy.</span>
              </div>
            )}
            <div className="job-encryption-note">
              <span aria-hidden="true">◆</span>
              <div>
                <strong>Client-side envelope encryption</strong>
                <small>Cyrus stores only ciphertext, opaque IDs, timing, and delivery state.</small>
              </div>
            </div>
            {error && <div className="jobs-error" role="alert">{error}</div>}
            <button
              className="job-publish"
              type="submit"
              disabled={!canSubmit || busy !== null || installation.agentLoopStatus !== 'ready'}
            >
              {busy === 'publish' ? 'Encrypting and publishing…' : 'Publish job'}
              {busy !== 'publish' && <span aria-hidden="true">→</span>}
            </button>
          </form>

          <div className="job-monitor">
            <section className="agent-output-sink" aria-label="Agent output">
              <header>
                <div>
                  <span className="eyebrow">Live, client-decrypted</span>
                  <h3>Agent output</h3>
                </div>
                <div className="agent-output-actions">
                  {selectedJob && (
                    <span className={`job-status ${selectedJob.status}`}>
                      <i />{statusLabel(selectedJob.status)}
                    </span>
                  )}
                  <button
                    type="button"
                    disabled={outputEntries.length === 0}
                    onClick={() => void copyOutput()}
                  >
                    {copyState === 'copied'
                      ? 'Copied'
                      : copyState === 'failed'
                        ? 'Copy failed'
                        : 'Copy output'}
                  </button>
                </div>
              </header>
              {selectedJob ? (
                <>
                  <div className="agent-output-context">
                    <strong>{typeLabel(selectedJob.jobType)}</strong>
                    <code>{shortId(selectedJob.sessionId)}</code>
                    <span>Following automatically</span>
                  </div>
                  <div
                    className="agent-output-stream"
                    role="log"
                    aria-label="Live agent output"
                    aria-live="polite"
                    aria-relevant="additions text"
                  >
                    {outputEntries.map((entry) => (
                      <article
                        className={`agent-output-entry ${entry.role}`}
                        key={entry.id}
                      >
                        <header>
                          <strong>{entry.label}</strong>
                          <time dateTime={entry.createdAt}>
                            {relativeTime(entry.createdAt)}
                          </time>
                        </header>
                        <p>{entry.text}</p>
                      </article>
                    ))}
                    {outputEntries.length === 0 && !outputError && (
                      <div className="agent-output-waiting">
                        <i />
                        <strong>{activeStatuses.has(selectedJob.status)
                          ? 'Waiting for encrypted output…'
                          : 'No agent text was emitted for this job.'}</strong>
                        <span>New events appear here automatically.</span>
                      </div>
                    )}
                    {outputError && (
                      <div className="agent-output-problem" role="alert">
                        <strong>Output temporarily unavailable</strong>
                        <span>{outputError}</span>
                      </div>
                    )}
                    <div ref={outputEnd} />
                  </div>
                  <div className="agent-output-privacy">
                    <span aria-hidden="true">◆</span>
                    Decryption happens only in this browser. Cyrus stores ciphertext.
                  </div>
                </>
              ) : (
                <div className="agent-output-empty">
                  <span>◇</span>
                  <strong>Start a session to see its output</strong>
                  <p>The newest job is selected and followed automatically.</p>
                </div>
              )}
            </section>

            <section className="job-ledger" aria-label="Customer job history">
              <header>
                <div>
                  <span className="eyebrow">Last 24 hours</span>
                  <h3>Delivery ledger</h3>
                </div>
                <button type="button" onClick={() => void refresh()}>Refresh</button>
              </header>
              <div className="job-list">
                {jobs.map((job) => (
                  <article
                    className={`job-card ${selectedJobId === job.jobId ? 'selected' : ''}`}
                    key={job.jobId}
                  >
                    <div className="job-card-top">
                      <span className={`job-status ${job.status}`}><i />{statusLabel(job.status)}</span>
                      <time dateTime={job.createdAt}>{relativeTime(job.createdAt)}</time>
                    </div>
                    <strong>{typeLabel(job.jobType)}</strong>
                    <code>{shortId(job.sessionId)}</code>
                    <dl>
                      <div><dt>Attempt</dt><dd>{job.attemptCount} / {job.maximumAttempts}</dd></div>
                      <div><dt>Source</dt><dd>{job.sourceType.toUpperCase()}</dd></div>
                      <div><dt>Payload</dt><dd>{job.payloadBytes ? `${Math.ceil(job.payloadBytes / 1024)} KB encrypted` : 'None'}</dd></div>
                    </dl>
                    {job.errorCode && <div className="job-error-code">{job.errorCode}</div>}
                    <div className="job-actions">
                      <button
                        type="button"
                        aria-pressed={selectedJobId === job.jobId}
                        onClick={() => setSelectedJobId(job.jobId)}
                      >
                        {selectedJobId === job.jobId ? 'Following output' : 'View output'}
                      </button>
                      {activeStatuses.has(job.status) && (
                        <button
                          type="button"
                          className="danger"
                          disabled={busy === job.jobId}
                          onClick={() => void mutate(job, 'cancel')}
                        >
                          Cancel
                        </button>
                      )}
                      {terminalStatuses.has(job.status) && (
                        <button
                          type="button"
                          disabled={busy === job.jobId}
                          onClick={() => void mutate(job, 'dismiss')}
                        >
                          Dismiss
                        </button>
                      )}
                    </div>
                  </article>
                ))}
                {jobs.length === 0 && (
                  <div className="jobs-empty">
                    <span>◇</span>
                    <strong>No jobs yet</strong>
                    <p>Start a session to create the first encrypted VPC job.</p>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </section>
    </div>
  );
}

export type DecryptedEvent = {
  id: number;
  eventType: string;
  createdAt: string;
  kind: 'user' | 'assistant' | 'tool' | 'status' | 'error' | 'encrypted';
  name: string | null;
  groupId: string | null;
  text: string;
  unavailable: boolean;
};

export type AgentOutputEntry = {
  id: string;
  role: DecryptedEvent['kind'];
  label: string;
  text: string;
  createdAt: string;
  groupId: string | null;
  final: boolean;
};

export async function decryptEvent(event: CustomerJobEvent): Promise<DecryptedEvent> {
  const value = await decryptCustomerJobEvent(event).catch(() => ({ unavailable: true }));
  if (isRecord(value) && value.unavailable === true) {
    return {
      id: event.id,
      eventType: event.eventType,
      createdAt: event.createdAt,
      kind: 'encrypted',
      name: null,
      groupId: null,
      text: 'This event was encrypted in another browser session and cannot be opened here.',
      unavailable: true,
    };
  }
  if (!isRecord(value)) {
    return {
      id: event.id,
      eventType: event.eventType,
      createdAt: event.createdAt,
      kind: 'status',
      name: event.eventType,
      groupId: null,
      text: lifecycleText(event.eventType),
      unavailable: false,
    };
  }
  const payload = isRecord(value.payload) ? value.payload : {};
  const kind = eventKind(value.kind);
  const name = typeof value.name === 'string' ? value.name : null;
  return {
    id: event.id,
    eventType: event.eventType,
    createdAt: event.createdAt,
    kind,
    name,
    groupId: stringValue(payload.messageId) ?? stringValue(payload.runId),
    text: eventText(kind, name, payload),
    unavailable: false,
  };
}

function eventText(
  kind: DecryptedEvent['kind'],
  name: string | null,
  payload: Record<string, unknown>,
): string {
  if (kind === 'assistant') {
    return stringValue(payload.text)
      ?? stringValue(payload.delta)
      ?? 'The agent emitted an empty response.';
  }
  if (kind === 'user') {
    return stringValue(payload.text) ?? 'A prompt was submitted.';
  }
  if (kind === 'error') {
    return stringValue(payload.message)
      ?? stringValue(payload.error)
      ?? humanize(name ?? 'Agent error');
  }
  if (kind === 'tool') {
    const tool = stringValue(payload.tool) ?? humanize(name ?? 'tool');
    const state = stringValue(payload.state);
    const error = stringValue(payload.error);
    if (error) return `${tool}: ${error}`;
    const result = stringValue(payload.result);
    if (result) return `${tool}: ${result}`;
    return state ? `${tool} · ${humanize(state)}` : tool;
  }
  if (stringValue(payload.message)) return stringValue(payload.message)!;
  if (name === 'model_usage') return 'Turn completed and usage was recorded in the customer VPC.';
  if (name === 'agent_start') return 'Agent started processing this turn.';
  if (name === 'agent_end') return 'Agent finished processing this turn.';
  return humanize(name ?? 'Agent status updated');
}

export function coalesceOutput(events: DecryptedEvent[]): AgentOutputEntry[] {
  const entries: AgentOutputEntry[] = [];
  const assistantByGroup = new Map<string, number>();
  for (const event of events) {
    if (event.kind === 'assistant') {
      const group = event.groupId ?? `assistant:${event.id}`;
      const existingIndex = assistantByGroup.get(group);
      const final = event.name === 'assistant_final';
      if (existingIndex !== undefined) {
        const existing = entries[existingIndex]!;
        entries[existingIndex] = {
          ...existing,
          id: `${existing.id}:${event.id}`,
          text: final ? event.text : `${existing.text}${event.text}`,
          createdAt: event.createdAt,
          final: existing.final || final,
        };
        continue;
      }
      assistantByGroup.set(group, entries.length);
      entries.push({
        id: String(event.id),
        role: event.kind,
        label: 'Agent',
        text: event.text,
        createdAt: event.createdAt,
        groupId: event.groupId,
        final,
      });
      continue;
    }
    entries.push({
      id: String(event.id),
      role: event.kind,
      label: outputLabel(event),
      text: event.text,
      createdAt: event.createdAt,
      groupId: event.groupId,
      final: true,
    });
  }
  return entries;
}

export function mergeEvents(
  existing: DecryptedEvent[],
  incoming: DecryptedEvent[],
): DecryptedEvent[] {
  const byId = new Map(existing.map((event) => [event.id, event]));
  for (const event of incoming) byId.set(event.id, event);
  return [...byId.values()].sort((left, right) => left.id - right.id);
}

function outputLabel(event: DecryptedEvent): string {
  if (event.kind === 'user') return 'You';
  if (event.kind === 'status') return 'Status';
  if (event.kind === 'tool') return 'Tool';
  if (event.kind === 'error') return 'Error';
  if (event.kind === 'encrypted') return 'Encrypted event';
  return event.eventType.startsWith('job.')
    ? humanize(event.eventType.slice(4))
    : 'Status';
}

function eventKind(value: unknown): DecryptedEvent['kind'] {
  return ['user', 'assistant', 'tool', 'status', 'error'].includes(String(value))
    ? value as DecryptedEvent['kind']
    : 'status';
}

function lifecycleText(eventType: string): string {
  const value = eventType.startsWith('job.') ? eventType.slice(4) : eventType;
  if (value === 'queued') return 'Job encrypted and queued for a customer worker.';
  if (value === 'leased') return 'A customer worker reserved this job.';
  if (value === 'started') return 'The customer worker started the agent turn.';
  if (value === 'succeeded') return 'The agent turn completed successfully.';
  if (value === 'failed') return 'The agent turn failed and may be retried.';
  if (value === 'retry_wait') return 'Waiting before the next automatic retry.';
  if (value === 'cancelled') return 'The job was cancelled.';
  return humanize(value);
}

function humanize(value: string): string {
  const normalized = value.replace(/[._-]+/gu, ' ').trim();
  return normalized ? normalized[0]!.toUpperCase() + normalized.slice(1) : 'Status updated';
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function shortId(value: string): string {
  return `session · ${value.slice(0, 8)}`;
}

function typeLabel(value: CustomerJobType): string {
  if (value === 'session.start') return 'Start isolated session';
  if (value === 'session.prompt') return 'Continue session';
  return 'Stop session';
}

function statusLabel(value: CustomerJob['status']): string {
  return value.replace('_', ' ');
}

function relativeTime(value: string): string {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 1000));
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}
